<template>
    <modal @close="emit('close')">
        <div class="w-[332px] flex flex-col gap-4">
            <button type="button" class="ds-glass-dialog-close" aria-label="Закрыть" @click="emit('close')">
                <icon width="18" height="18" vb-size="24"><IconClose /></icon>
            </button>
            <div class="ds-glass-dialog-title">{{ title }}</div>
            <!-- RD-160: одно поле периода с быстрым выбором вместо двух дат; payload date_from/date_to прежний. -->
            <div class="flex flex-col gap-1" :class="{ 'pointer-events-none opacity-70': readonly }">
                <div class="ds-glass-label">Период</div>
                <div class="ds-glass-date">
                    <AppDateInput mode="range" presets :model-value="period" :initial-date="dateFrom" @update:model-value="onPeriodSelect" />
                    <svg class="ds-glass-date-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
                </div>
            </div>
            <template v-if="isConsolidated">
                <!-- group_by: employee|position, is_outsource: false|true — только сводный отчёт. -->
                <div class="flex flex-col gap-1.5">
                    <div class="ds-glass-label">Группировать</div>
                    <div class="ds-glass-segment" role="group" aria-label="Группировать">
                        <button
                            v-for="option in groupOptions"
                            :key="option.label"
                            type="button"
                            class="ds-glass-segment-option"
                            :class="{ 'is-active': isGroupedByPosition === option.value }"
                            :aria-pressed="isGroupedByPosition === option.value"
                            :disabled="readonly"
                            @click="isGroupedByPosition = option.value"
                        >
                            {{ option.label }}
                        </button>
                    </div>
                </div>
                <div class="flex flex-col gap-1.5">
                    <div class="ds-glass-label">Сотрудники</div>
                    <div class="ds-glass-segment" role="group" aria-label="Сотрудники">
                        <button
                            v-for="option in outsourceOptions"
                            :key="option.label"
                            type="button"
                            class="ds-glass-segment-option"
                            :class="{ 'is-active': isOutsource === option.value }"
                            :aria-pressed="isOutsource === option.value"
                            :disabled="readonly"
                            @click="isOutsource = option.value"
                        >
                            {{ option.label }}
                        </button>
                    </div>
                </div>
            </template>
            <div class="ds-glass-dialog-actions !mt-1">
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="emit('close')">Отмена</button>
                <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="isLoading" @click="download">Скачать</button>
            </div>
        </div>
    </modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import AppDateInput from './AppDateInput.vue'
import IconClose from './icons/IconClose.vue'
import { useScheduleStore } from '@/stores/scheduleStore'

const props = withDefaults(defineProps<{
    title: string
    reportFn: (data: any) => Promise<any>
    reportName: string
    initialDate?: Dayjs
    readonly?: boolean
    isConsolidated?: boolean
}>(), {
    initialDate: () => dayjs(),
    readonly: false,
    isConsolidated: false,
})

const emit = defineEmits(['close'])

const scheduleStore = useScheduleStore()

const referenceDate = props.initialDate
const dateFrom = ref<Dayjs>(referenceDate.startOf('month'))
const dateTo = ref<Dayjs>(referenceDate.endOf('month'))
const period = computed<[Dayjs, Dayjs]>(() => [dateFrom.value, dateTo.value])
const isLoading = ref(false)
const isGroupedByPosition = ref(false)
const isOutsource = ref(false)

const groupOptions = [
    { label: 'По ФИО', value: false },
    { label: 'По должности', value: true },
]
// AppDateInput (range) отдаёт только завершённый период: второй клик или быстрый выбор.
function onPeriodSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    const [start, end] = value as [Dayjs, Dayjs]
    if (!start || !end) return
    dateFrom.value = start
    dateTo.value = end
}

const outsourceOptions = [
    { label: 'Штат', value: false },
    { label: 'Штат и аутсорс', value: true },
]

async function download() {
    isLoading.value = true
    try {
        const data = {
            department_id: scheduleStore.currentDepartment?.id,
            date_from: dateFrom.value.format('YYYY-MM-DD'),
            date_to: dateTo.value.format('YYYY-MM-DD'),
            ...(props.isConsolidated && {
                group_by: isGroupedByPosition.value ? 'position' : 'employee',
                is_outsource: isOutsource.value,
            }),
        }
        const res = await props.reportFn(data)
        const blob = new Blob([res.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${props.reportName} ${data.date_from}—${data.date_to}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        emit('close')
    } finally {
        isLoading.value = false
    }
}
</script>
